import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Popover from "@radix-ui/react-popover";
import Check from "lucide-react/dist/esm/icons/check";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import Search from "lucide-react/dist/esm/icons/search";
import { funnel } from "remeda";

import { Project } from "@/types/medplum";
import { ANALYTICS_EVENTS } from "@/constants/analytics.constants";
import { posthog } from "@/lib/posthog";
import { cn } from "@/lib/utils";
import { useProject } from "@/context/project-provider";

import { Avatar } from "@/components/base/Avatar";

export const OrgSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const { projectList, project, selectProject, loading } = useProject();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const activeOrgRef = useRef<HTMLButtonElement>(null);

  const searchFunnel = useMemo(
    () =>
      funnel(
        (searchQuery: string) =>
          posthog.capture(ANALYTICS_EVENTS.ORGANIZATION_SEARCH, {
            queryLength: searchQuery.length,
          }),
        {
          reducer: (_previous: string | undefined, searchQuery: string) =>
            searchQuery,
          minGapMs: 60_000,
          triggerAt: "start",
        }
      ),
    []
  );

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      setQuery("");
    }
  };

  const handleQueryChange = (nextQuery: string) => {
    setQuery(nextQuery);
    searchFunnel.call(nextQuery);
  };

  const filtered = projectList.filter((org) =>
    org.name?.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = (org: Project) => {
    handleOpenChange(false);

    if (org.id === project?.id) {
      return;
    }
    void selectProject(org);
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={loading}
          className={cn(
            "flex h-9 cursor-pointer items-center gap-2 rounded-lg",
            "border-border bg-card border",
            "text-text px-2.5 text-[13px] font-medium",
            "hover:bg-surface-hovered",
            "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none"
          )}
        >
          <Avatar size="xs" initials={project?.name ?? "?"} />
          <span className="max-w-[160px] truncate">
            {project?.name ?? t("Select organization")}
          </span>
          <ChevronDown
            className={cn(
              "text-subdued h-4 w-4 transition-transform",
              open && "rotate-180"
            )}
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={() => {
            activeOrgRef.current?.scrollIntoView({ block: "center" });
          }}
          className={cn(
            "app-root animate-overlay-in z-50 w-[280px] overflow-hidden",
            "border-border rounded-lg border",
            "bg-card shadow-popover"
          )}
        >
          <div className="border-border flex items-center gap-2 border-b px-3 py-2">
            <Search className="text-subdued h-3.5 w-3.5" />
            <input
              value={query}
              onChange={(changeEvent) =>
                handleQueryChange(changeEvent.target.value)
              }
              placeholder={t("Search organizations")}
              className="placeholder:text-disabled w-full bg-transparent text-[13px] outline-none"
            />
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="text-subdued px-3 py-2 text-xs">
                {t("No organizations match")}
              </p>
            ) : (
              filtered.map((org) => {
                const { id, name } = org;
                const isActive = id === project?.id;

                return (
                  <button
                    key={id}
                    ref={isActive ? activeOrgRef : null}
                    onClick={() => handleSelect(org)}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-[13px]",
                      "hover:bg-surface-hovered",
                      isActive && "bg-surface-selected"
                    )}
                  >
                    <Avatar size="xs" initials={name ?? "?"} />
                    <span className="flex-1 truncate">{name ?? id}</span>
                    {isActive && <Check className="text-primary h-3.5 w-3.5" />}
                  </button>
                );
              })
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
